import {store} from '../store';
import {Environment, startModalLoading, stopModalLoading, updateEnvironment} from '../reducers/environmentReducer';
import {PeachPayOrder} from '../reducers/peachPayOrderReducer';
import {requestCartCalculation} from '../payment/order';
import {type LoadingMode, type ModalPage} from '../models/IEnvironment';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {cartIsVirtual} from '../util/cart';
import {Carts, DefaultCart} from '../reducers/cartReducer';
import {getLocaleText} from '../util/translation';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {renderModalPage} from './modalPage';
import {backToInfo, backToShipping, continueToPayment, dispatchRequestedPage, renderModalNavigation, renderModalPageIndicator} from './navigation';
import {eventClick} from '../util/dom';
import {type ButtonClickedMessage, type InitMessage} from '../../../@type/window-messages';
import {$qs, onWindowMessage, $qsAll} from '../../../@shared/ts/dom';
import {PaymentConfiguration} from '../reducers/paymentConfigurationReducer';

export function initModal(message: InitMessage) {
	initPageSwitch();
	insertCustomCheckoutCSS(message);
	// Keep modal state rendering up to date
	let prevCurrencyCode = '';
	store.subscribe(() => {
		if (Environment.modalUI.open()) {
			$qs('#pp-modal-content', $element => {
				$element.style.display = 'flex';
			});
		} else if (!Environment.modalUI.open()) {
			$qs('#pp-modal-content', $element => {
				$element.style.display = 'none';
			});
			return;
		}

		renderButtonColorTheme(Environment.plugin.buttonColor());
		renderTestModeBannerDisplay(Environment.testMode());
		renderModalPageIndicator(Environment.modalUI.page());
		renderModalNavigation(Environment.modalUI.page());
		renderContinueButtonDisplay(Environment.modalUI.page());
		renderContinueError(
			Environment.modalUI.page(),
			PeachPayOrder.errorMessage(),
		);
		renderContinueButtonLoading(Environment.modalUI.loadingMode());
		ppDisableLoadingMode(Environment.modalUI.loadingMode());
		ppBlurOnRecalculate(
			Environment.modalUI.loadingMode(),
			prevCurrencyCode !== MerchantConfiguration.currency.code(),
		);
		prevCurrencyCode = MerchantConfiguration.currency.code();

		renderModalPage(Environment.modalUI.page());
		renderOneClickCheckbox(
			Environment.modalUI.page(),
		);
		renderTermsAndCondition(
			Environment.modalUI.page(),
			message.phpData?.wc_terms_conditions ?? '',
		);
		skipShippingPage();
		renderHideOnMobile(Environment.customer.mobile());

		renderFreeOrderDisplay(DefaultCart.contents().length, Carts.total());
		displayErrorMessage(PeachPayOrder.errorMessage(), DefaultCart.contents().length, Carts.total());
		renderSummaryBorder();
	});

	onWindowMessage('UI::modalOpened', _ => {
		store.dispatch(updateEnvironment({
			modalIsOpen: true,
		}));
	});

	onWindowMessage('UI::modalClosed', _ => {
		store.dispatch(updateEnvironment({
			modalIsOpen: false,
		}));

		store.dispatch(stopModalLoading());
	});

	// Event when the peachpay button is clicked
	onWindowMessage<ButtonClickedMessage>('buttonClicked', async () => {
		openCheckoutModal();

		store.dispatch(startModalLoading());
		await requestCartCalculation(true);
		store.dispatch(stopModalLoading());
	});

	$qsAll('.pp-exit', (element: HTMLElement) => {
		element.addEventListener('click', requestCloseModal);
	});
	$qs('.pp-close')?.addEventListener('click', requestCloseModal); // Default click behavior.
	$qs('.pp-close')?.addEventListener('keypress', requestCloseModal); // Keypress for accessibility.

	$qs('#edit-info', ($element: HTMLElement) => {
		$element.addEventListener('click', async event => {
			await backToShipping(event, true);
		});
	});
	$qsAll('.pp-back-to-info', (element: HTMLElement) => {
		element.addEventListener('click', backToInfo);
	});
	$qsAll('.pp-back-to-shipping', (element: HTMLElement) => {
		element.addEventListener('click', backToShipping);
	});

	$qs('#pp-continue-to-payment')?.addEventListener('click', continueToPayment);
	$qs('#pp-continue-to-payment-mobile')?.addEventListener('click', continueToPayment);
}

/**
 * Hides the prices by bluring them until we can show the real amount they will be
 *
 * @param loadingMode The current loading state of the modal
 */
function ppBlurOnRecalculate(
	loadingMode: LoadingMode,
	currencyChanged: boolean,
) {
	if (loadingMode === 'loading') {
		$qsAll('.pp-recalculate-blur', element => {
			element.classList.add('pp-blur');
		});
		// Currently this does not visibily work but requires shipping.ts refactor
		if (currencyChanged) {
			$qsAll('.pp-currency-blur', element => {
				element.classList.add('pp-blur');
			});
		}
	} else {
		$qsAll('.pp-recalculate-blur', element => {
			element.classList.remove('pp-blur');
		});
		$qsAll('.pp-currency-blur', element => {
			element.classList.remove('pp-blur');
		});
	}
}

function renderFreeOrderDisplay(cartCount: number, allCartsTotal: number) {
	if (cartCount > 0 && allCartsTotal === 0) {
		$qsAll('.pp-hide-on-free-order', $el => {
			$el.classList.add('hide');
		});
	} else {
		$qsAll('.pp-hide-on-free-order', $el => {
			$el.classList.remove('hide');
		});
	}
}

function displayErrorMessage(errorMessage: string, cartCount: number, allCartsTotal: number) {
	if (errorMessage === '') {
		if (cartCount <= 0 && allCartsTotal !== 0) {
			$qs('#pp-pms-new-container')?.classList.remove('hide');
		}

		for (const $element of $qsAll('.hide-when-invalid')) {
			$element.classList.remove('hide');
		}

		$qs('#invalid-order-message')?.classList.add('hide');
	} else {
		if (PaymentConfiguration.eligibleGatewayCount() === 0 || cartCount === 0) {
			$qs('#pp-pms-new-container')?.classList.add('hide');
		}

		for (const $element of $qsAll('.hide-when-invalid')) {
			$element.classList.add('hide');
		}

		$qs('#invalid-order-message')?.classList.remove('hide');

		$qs('#invalid-order-message', $element => {
			$element.innerHTML = errorMessage;
		});
	}
}

function openCheckoutModal(): void {
	window.top?.postMessage('openModal', location.origin);
}

/**
 * Request the plugin javascript to close the modal.
 */
function requestCloseModal(event: MouseEvent | KeyboardEvent): void {
	if (!eventClick(event)) {
		return;
	}

	// This tells the parent to hide the overlay
	window.top?.postMessage('closeModal', location.origin);
	// Hide cart notices so it doesn't reappear when checkout modal reopens.
	$qsAll('.pp-notice-container', $el => {
		$el.classList.add('hide');
	});
}

function renderContinueButtonDisplay(modalPage: ModalPage) {
	if (modalPage === 'info') {
		$qs('#pp-continue-to-shipping-mobile')?.classList.remove('hide');
		$qs('#pp-continue-to-payment-mobile')?.classList.add('hide');
		$qs('.pay-button-container-mobile')?.classList.add('hide');
	} else if (modalPage === 'shipping') {
		$qs('#pp-continue-to-shipping-mobile')?.classList.add('hide');
		$qs('#pp-continue-to-payment-mobile')?.classList.remove('hide');
		$qs('.pay-button-container-mobile')?.classList.add('hide');
	} else if (modalPage === 'payment') {
		$qs('#pp-continue-to-shipping-mobile')?.classList.add('hide');
		$qs('#pp-continue-to-payment-mobile')?.classList.add('hide');
		$qs('.pay-button-container-mobile')?.classList.remove('hide');
	}
}

/**
 * If we come across a error on the information page screen we don't want customers to have the ability to continue as normal as it might upset them we let the continue only to have them
 * not able to pay this way we can ensure that they can't continue to the pay screen by rendering order errors instead of the continue button.
 * @param modalPage current modal page
 * @param errorMessage The error message to display
 */
function renderContinueError(modalPage: ModalPage, errorMessage: string) {
	$qsAll('.pp-continue-order-error', element => {
		element.innerHTML = '';
		element.classList.remove('pp-error');
	});
	if ((modalPage === 'info' || modalPage === 'shipping') && errorMessage !== '') {
		$qsAll('.pp-continue-order-error', element => {
			element.innerHTML = errorMessage;
			element.classList.add('pp-error');
		});
	}
}

function renderContinueButtonLoading(loadingMode: LoadingMode) {
	if (loadingMode === 'loading') {
		$qs<HTMLInputElement>('#pp-continue-to-shipping', $element => {
			$element.disabled = true;
		});
		$qs<HTMLInputElement>('#pp-continue-to-payment', $element => {
			$element.disabled = true;
		});
		$qs<HTMLInputElement>('#pp-continue-to-shipping-mobile', $element => {
			$element.disabled = true;
		},
		);

		$qs('#continue-spinner-shipping')?.classList.remove('hide');
		$qs('#continue-spinner-payment')?.classList.remove('hide');
		$qs('#continue-spinner-shipping-mobile')?.classList.remove('hide');
		$qs('#continue-spinner-payment-mobile')?.classList.remove('hide');
		$qsAll('.modal-lock-icon', $element => {
			$element.classList.add('hide');
		});
	} else {
		$qs<HTMLInputElement>('#pp-continue-to-shipping', $element => {
			$element.disabled = false;
		});

		if (!PeachPayCustomer.shipToDifferentAddress() || Carts.anyShippingMethodsAvailable()) {
			$qs<HTMLInputElement>(
				'#pp-continue-to-payment',
				$element => {
					$element.disabled = false;
				},
			);
		} else if (!Carts.anyShippingMethodsAvailable()) {
			renderContinueError('shipping', getLocaleText('There are no shipping methods available'));
		}

		$qs<HTMLInputElement>(
			'#pp-continue-to-shipping-mobile',
			$element => {
				$element.disabled = false;
			},
		);
		$qs('#continue-spinner-shipping')?.classList.add('hide');
		$qs('#continue-spinner-payment')?.classList.add('hide');
		$qs('#continue-spinner-shipping-mobile')?.classList.add('hide');
		$qs('#continue-spinner-payment-mobile')?.classList.add('hide');
		$qsAll('.modal-lock-icon', $element => {
			$element.classList.remove('hide');
		});
	}
}

function ppDisableLoadingMode(loadingMode: LoadingMode) {
	if (loadingMode === 'loading') {
		$qsAll<HTMLInputElement>('.pp-disabled-loading', $element => {
			$element.classList.add('pp-disabled');
			$element.classList.add('pp-mode-loading');
		});
	} else if (loadingMode === 'processing') {
		$qsAll<HTMLInputElement>('.pp-disabled-processing', $element => {
			$element.classList.add('pp-disabled');
		});
	} else {
		$qsAll<HTMLInputElement>('.pp-disabled-processing,.pp-disabled-loading', $element => {
			$element.classList.remove('pp-disabled');
			$element.classList.remove('pp-mode-loading');
		});
	}
}

/**
 * Renders the test mode banner around the modal.
 */
function renderTestModeBannerDisplay(testMode: boolean): void {
	if (testMode) {
		$qs('.pp-container')?.classList.add('pp-test-mode');
	}
}

/**
 * Updates the modals colors to match the merchants button color.
 */
function renderButtonColorTheme(color = '#21105d'): void {
	// Changes a css variable which is used in css classes.
	document.documentElement.style.setProperty('--peachpay-theme-color', color);
	// ~50% alpha
	document.documentElement.style.setProperty(
		'--peachpay-theme-color-opaque',
		color + '80',
	);
	// ~12% alpha
	document.documentElement.style.setProperty(
		'--peachpay-theme-color-light',
		color + '20',
	);
}

/* Event handler for the Continue button on billing page. */
function dispatchPayment() {
	dispatchRequestedPage('payment');
}

/* If the cart is virtual and there are no additional fields or related products, skip the shipping page. */
function skipShippingPage(): void {
	const shouldSkipShippingPage = cartIsVirtual(DefaultCart.contents()) && $qs('#additional-fields-new')?.classList.contains('hide');
	if (shouldSkipShippingPage) {
		$qs('#pp-checkout-status-middle')?.classList.add('hide');
		$qsAll('#pp-continue-to-shipping, #pp-continue-to-shipping-mobile').forEach(($el: HTMLElement) => {
			$el.addEventListener('click', dispatchPayment);
		});
		$qsAll('.pp-back-to-shipping', (element: HTMLElement) => {
			element.addEventListener('click', backToInfo);
		});

		if (Environment.modalUI.page() === 'shipping') {
			backToInfo();
		}
	} else {
		$qs('#pp-checkout-status-middle')?.classList.remove('hide');
		$qsAll('#pp-continue-to-shipping, #pp-continue-to-shipping-mobile').forEach(($el: HTMLElement) => {
			$el.removeEventListener('click', dispatchPayment);
		});
		$qsAll('.pp-back-to-shipping', (element: HTMLElement) => {
			element.removeEventListener('click', backToInfo);
		});
	}
}

function renderHideOnMobile(isMobile: boolean) {
	if (isMobile) {
		$qsAll('.pp-hide-on-mobile', $el => {
			$el.classList.add('hide');
		});
	} else {
		$qsAll('.pp-hide-on-mobile', $el => {
			$el.classList.remove('hide');
		});
	}
}

/**
 * Renders the Terms and Conditions section above the pay button.
 */
function renderTermsAndCondition(page: ModalPage, terms?: string) {
	if (page === 'payment') {
		// eslint-disable-next-line
		const merchantTermsConditions = terms ? `${getLocaleText("the store's")} <a href='${terms}' target='_blank'>${getLocaleText('terms and conditions')}</a> ${getLocaleText('and')}` : '';
		$qsAll('.pp-tc-section', $el => {
			$el.innerHTML = '';

			$el.innerHTML += `<label class='pp-tc-contents'>${getLocaleText('By clicking the button above, you agree to')} ${merchantTermsConditions} ${getLocaleText('the')} PeachPay <a href='https://peachpay.app/terms' target='_blank'>${getLocaleText('terms')}</a> ${getLocaleText('and')} <a href='https://peachpay.app/privacy' target='_blank'>${getLocaleText('privacy policy')}</a>.</label>`;
			$el.classList.remove('hide');
		});
	} else {
		$qsAll('.pp-tc-section', $el => {
			$el.classList.add('hide');
		});
	}
}

/**
 * Renders the save payment info checkbox.
 */
function renderOneClickCheckbox(page: ModalPage) {
	if (page === 'payment') {
		$qsAll('.pp-save-payment-info-section', $el => {
			$el.classList.remove('hide');
		});
	} else {
		$qsAll('.pp-save-payment-info-section', $el => {
			$el.classList.add('hide');
		});
	}
}

function insertCustomCheckoutCSS(message: InitMessage) {
	const $body = document.querySelector('body');
	if ($body) {
		const $style = document.createElement('style');
		$style.id = 'pp-custom-checkout-css';
		$style.appendChild(
			document.createTextNode(
				message.phpData.custom_checkout_css?.trim() ?? '',
			),
		);

		$body.insertAdjacentElement('beforeend', $style);
	}
}

function initPageSwitch() {
	$qs('#pp-continue-to-shipping')?.addEventListener('click', () => {
		dispatchRequestedPage('shipping');
		$qs('.pp-close')?.scrollIntoView();
	});
	$qs('#pp-continue-to-shipping-mobile')?.addEventListener('click', () => {
		dispatchRequestedPage('shipping');
		$qs('.pp-close')?.scrollIntoView();
	});
	$qs('#pp-checkout-status-middle')?.addEventListener('click', () => {
		dispatchRequestedPage('shipping');
		$qs('.pp-close')?.scrollIntoView();
	});
	$qs('#pp-checkout-status-right')?.addEventListener('click', () => {
		dispatchRequestedPage('payment');
		$qs('.pp-close')?.scrollIntoView();
	});
}

function renderSummaryBorder() {
	let itemsHeight = 0;
	$qs('#pp-summary-body', $parent => {
		const $children = $parent?.querySelectorAll('.pp-order-summary-item');
		if (!$children) {
			return;
		}

		for (const $child of Array.from($children)) {
			itemsHeight += $child?.clientHeight ?? 0;
		}

		if ($parent?.clientHeight < itemsHeight) {
			$parent.classList.add('pp-review-border');
		} else {
			$parent.classList.remove('pp-review-border');
		}
	});
}
